import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from "react";
import { ThemeProvider as MuiThemeProvider, createTheme } from "@mui/material/styles";
import axiosInstance from "../axiosInstance";
import { getSubdomainPrefix } from "../utils/subdomain";
import {
  parseColor,
  darkenColor,
  lightenColor,
  toRgbaString,
  generateGradient,
  isValidColor,
  getContrastTextColor,
  getGradientContrastTextColor,
} from "../utils/colorUtils";

const DEFAULT_PORTAL_COLOR = "#4f46e5";
const DEFAULT_LOGIN_COLOR = "#0A6836";

const ThemeContext = createContext({
  themeColor: null,
  config: {},
  employer: null,
  loadingTheme: false,
  updateTheme: () => {},
});

export const useTheme = () => useContext(ThemeContext);

// Initial synchronous read from localStorage to avoid visual flicker on refresh
const getInitialTheme = () => {
  try {
    const storedUserStr = localStorage.getItem("user");
    if (storedUserStr) {
      const user = JSON.parse(storedUserStr);
      return user?.colorTheme || user?.config?.themeColor || null;
    }
  } catch (e) {
    // ignore
  }
  return null;
};

const getInitialConfig = () => {
  try {
    const storedUserStr = localStorage.getItem("user");
    if (storedUserStr) {
      const user = JSON.parse(storedUserStr);
      return user?.config || {};
    }
  } catch (e) {
    // ignore
  }
  return {};
};

export function ThemeProvider({ children }) {
  const [themeColor, setThemeColor] = useState(getInitialTheme);
  const [config, setConfig] = useState(getInitialConfig);
  const [employer, setEmployer] = useState(null);
  const [loadingTheme, setLoadingTheme] = useState(false);

  // Apply CSS custom properties globally on :root
  const applyThemeProperties = useCallback((color, grad1, grad2, type = "solid", customTextColor = null) => {
    const isGradient = type === "gradient" && grad1 && grad2;
    const baseColor = isGradient ? grad1 : color || DEFAULT_PORTAL_COLOR;
    const parsed = parseColor(baseColor);
    const baseHex = parsed ? parsed.hex : baseColor;

    const hoverColor = darkenColor(baseHex, 15);
    const lightColor = toRgbaString(baseHex, 0.12);
    const shadowColor = toRgbaString(baseHex, 0.35);

    // If gradient mode, generate linear gradient from grad1 and grad2; if solid mode, generate elegant gradient from baseHex
    const gradient = isGradient
      ? generateGradient(grad1, grad2)
      : `linear-gradient(135deg, ${darkenColor(baseHex, 12)} 0%, ${baseHex} 100%)`;

    const g1 = isGradient ? parseColor(grad1)?.hex || grad1 : baseHex;
    const g2 = isGradient ? parseColor(grad2)?.hex || grad2 : lightenColor(baseHex, 15);

    // Prioritize explicit customTextColor from employer config; fallback to automatic luminance calculation
    const autoContrast = isGradient
      ? getGradientContrastTextColor(grad1, grad2)
      : getContrastTextColor(baseHex);
    const contrastColor =
      customTextColor && isValidColor(customTextColor)
        ? customTextColor
        : autoContrast;

    const contrastSubColor = toRgbaString(contrastColor, 0.85);
    const contrastOverlay = toRgbaString(contrastColor, 0.18);

    document.documentElement.style.setProperty("--primary-color", baseHex);
    document.documentElement.style.setProperty("--primary-hover", hoverColor);
    document.documentElement.style.setProperty("--primary-light", lightColor);
    document.documentElement.style.setProperty("--primary-shadow", shadowColor);
    document.documentElement.style.setProperty("--primary-gradient", gradient);
    document.documentElement.style.setProperty("--gradient-color-1", g1);
    document.documentElement.style.setProperty("--gradient-color-2", g2);
    document.documentElement.style.setProperty("--theme-type", type);
    document.documentElement.style.setProperty("--primary-contrast", contrastColor);
    document.documentElement.style.setProperty("--primary-contrast-sub", contrastSubColor);
    document.documentElement.style.setProperty("--primary-contrast-overlay", contrastOverlay);
  }, []);

  // Update theme manually (e.g. from Settings or Login)
  const updateTheme = useCallback(
    (newColor, newConfig = {}, employerInfo = null) => {
      const type = newConfig?.themeType || config?.themeType || "solid";
      const color = newColor || themeColor;
      const grad1 =
        newConfig?.gradientColor1 !== undefined
          ? newConfig.gradientColor1
          : config?.gradientColor1;
      const grad2 =
        newConfig?.gradientColor2 !== undefined
          ? newConfig.gradientColor2
          : config?.gradientColor2;
      const textColor =
        newConfig?.textColor !== undefined
          ? newConfig.textColor
          : config?.textColor;

      if (newColor) {
        setThemeColor(newColor);
      }
      if (newConfig) {
        setConfig((prev) => ({ ...prev, ...newConfig }));
      }
      if (employerInfo) {
        setEmployer(employerInfo);
      }

      applyThemeProperties(color, grad1, grad2, type, textColor);

      // Update stored user if logged in
      try {
        const storedUserStr = localStorage.getItem("user");
        if (storedUserStr) {
          const user = JSON.parse(storedUserStr);
          user.colorTheme = color;
          user.config = {
            ...(user.config || {}),
            ...(newConfig || {}),
            themeType: type,
            themeColor: color,
            gradientColor1: grad1,
            gradientColor2: grad2,
            textColor,
          };
          localStorage.setItem("user", JSON.stringify(user));
        }
      } catch (e) {
        // ignore
      }
    },
    [themeColor, config, applyThemeProperties]
  );

  // Apply CSS variables synchronously whenever themeColor or config changes
  useEffect(() => {
    if (themeColor) {
      applyThemeProperties(
        themeColor,
        config?.gradientColor1,
        config?.gradientColor2,
        config?.themeType || "solid",
        config?.textColor
      );
    }
  }, [themeColor, config, applyThemeProperties]);

  // Dynamic Theme Loader: runs on every page load / refresh
  useEffect(() => {
    const loadThemeFromSource = async () => {
      const host = window.location.hostname;
      const subdomain = getSubdomainPrefix(host);

      // 1. If subdomain is detected in URL or query parameter, fetch from public API
      if (subdomain) {
        try {
          setLoadingTheme(true);
          const res = await axiosInstance.get(`/employers/subdomain/${subdomain}`);
          if (res.data?.success) {
            const emp = res.data.employer;
            const cfg = res.data.config || {};
            const type = res.data.themeType || cfg.themeType || (res.data.gradientColor1 && res.data.gradientColor2 ? "gradient" : "solid");
            const color = res.data.themeColor || cfg.themeColor || emp?.colorTheme;
            const grad1 = res.data.gradientColor1 || cfg.gradientColor1;
            const grad2 = res.data.gradientColor2 || cfg.gradientColor2;
            const textColor = res.data.textColor || cfg.textColor;

            setEmployer(emp);
            setConfig({ ...cfg, themeType: type, textColor });
            if (color) {
              setThemeColor(color);
            }
            applyThemeProperties(color, grad1, grad2, type, textColor);

            // Update localStorage user if matching
            try {
              const storedUserStr = localStorage.getItem("user");
              if (storedUserStr) {
                const user = JSON.parse(storedUserStr);
                if (user.prefix?.toUpperCase() === emp.prefix?.toUpperCase()) {
                  user.colorTheme = color;
                  user.config = { ...cfg, themeType: type, textColor };
                  localStorage.setItem("user", JSON.stringify(user));
                }
              }
            } catch (e) {}
          }
        } catch (err) {
          console.warn("Could not load employer config from subdomain:", err?.message);
        } finally {
          setLoadingTheme(false);
        }
        return;
      }

      // 2. If NO subdomain in URL, check stored user in localStorage
      try {
        const storedUserStr = localStorage.getItem("user");
        if (storedUserStr) {
          const user = JSON.parse(storedUserStr);
          const localType = user?.config?.themeType || "solid";
          const localColor = user?.colorTheme || user?.config?.themeColor;
          const localGrad1 = user?.config?.gradientColor1;
          const localGrad2 = user?.config?.gradientColor2;
          const localTextColor = user?.config?.textColor;

          if (localColor) {
            setThemeColor(localColor);
            setConfig(user?.config || {});
            applyThemeProperties(localColor, localGrad1, localGrad2, localType, localTextColor);
          }

          // Fetch fresh config from backend using user.prefix or employerId!
          const tenantPrefix = user?.prefix;
          if (tenantPrefix && tenantPrefix !== "SADM") {
            setLoadingTheme(true);
            const res = await axiosInstance.get(`/employers/subdomain/${tenantPrefix}`);
            if (res.data?.success) {
              const emp = res.data.employer;
              const cfg = res.data.config || {};
              const type = res.data.themeType || cfg.themeType || (res.data.gradientColor1 && res.data.gradientColor2 ? "gradient" : "solid");
              const freshColor = res.data.themeColor || cfg.themeColor || emp?.colorTheme;
              const freshGrad1 = res.data.gradientColor1 || cfg.gradientColor1;
              const freshGrad2 = res.data.gradientColor2 || cfg.gradientColor2;
              const freshTextColor = res.data.textColor || cfg.textColor;

              setEmployer(emp);
              setConfig({ ...cfg, themeType: type, textColor: freshTextColor });
              if (freshColor) {
                setThemeColor(freshColor);
              }
              applyThemeProperties(freshColor, freshGrad1, freshGrad2, type, freshTextColor);

              // Update localStorage so next refresh has the updated color
              user.colorTheme = freshColor;
              user.config = { ...cfg, themeType: type, textColor: freshTextColor };
              localStorage.setItem("user", JSON.stringify(user));
            }
          }
        }
      } catch (err) {
        console.error("Error refreshing employer theme from API:", err);
      } finally {
        setLoadingTheme(false);
      }
    };

    loadThemeFromSource();
  }, [applyThemeProperties]);

  // Create dynamic Material-UI Theme so that all MUI buttons, chips, and components across the entire app use the theme color
  const muiTheme = useMemo(() => {
    const isGradient = config?.themeType === "gradient" && config?.gradientColor1;
    const baseColor =
      (isGradient ? config.gradientColor1 : themeColor) ||
      (window.location.pathname === "/login" ? DEFAULT_LOGIN_COLOR : DEFAULT_PORTAL_COLOR);
    const parsed = parseColor(baseColor);
    const activeHex = parsed?.hex || baseColor;
    const hoverHex = darkenColor(activeHex, 15);
    const lightHex = lightenColor(activeHex, 20);

    const autoContrast = isGradient
      ? getGradientContrastTextColor(config.gradientColor1, config.gradientColor2)
      : getContrastTextColor(activeHex);
    const contrastColor =
      config?.textColor && isValidColor(config.textColor)
        ? config.textColor
        : autoContrast;

    return createTheme({
      palette: {
        primary: {
          main: activeHex,
          dark: hoverHex,
          light: lightHex,
          contrastText: contrastColor,
        },
      },
      components: {
        MuiButton: {
          styleOverrides: {
            root: {
              textTransform: "none",
              transition: "transform 0.25s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.25s cubic-bezier(0.4, 0, 0.2, 1), filter 0.25s cubic-bezier(0.4, 0, 0.2, 1), background-color 0.25s ease !important",
            },
            contained: {
              color: `${contrastColor} !important`,
              transition: "transform 0.25s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.25s cubic-bezier(0.4, 0, 0.2, 1), filter 0.25s cubic-bezier(0.4, 0, 0.2, 1), background-color 0.25s ease !important",
              "&:hover": {
                filter: "brightness(0.92)",
                transform: "translateY(-1.5px)",
              },
              "&:active": {
                transform: "translateY(0)",
                filter: "brightness(0.85)",
              },
              "& .MuiSvgIcon-root": {
                color: `${contrastColor} !important`,
              },
            },
            containedPrimary: {
              backgroundColor: activeHex,
              color: `${contrastColor} !important`,
              transition: "transform 0.25s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.25s cubic-bezier(0.4, 0, 0.2, 1), filter 0.25s cubic-bezier(0.4, 0, 0.2, 1), background-color 0.25s ease !important",
              "&:hover": {
                backgroundColor: hoverHex,
                filter: "brightness(0.92)",
                transform: "translateY(-1.5px)",
              },
              "&:active": {
                transform: "translateY(0)",
                filter: "brightness(0.85)",
              },
              "& .MuiSvgIcon-root": {
                color: `${contrastColor} !important`,
              },
            },
          },
        },
        MuiCheckbox: {
          styleOverrides: {
            root: {
              "&.Mui-checked": {
                color: activeHex,
              },
            },
          },
        },
        MuiRadio: {
          styleOverrides: {
            root: {
              "&.Mui-checked": {
                color: activeHex,
              },
            },
          },
        },
        MuiSwitch: {
          styleOverrides: {
            switchBase: {
              "&.Mui-checked": {
                color: activeHex,
                "& + .MuiSwitch-track": {
                  backgroundColor: activeHex,
                },
              },
            },
          },
        },
        MuiTabs: {
          styleOverrides: {
            indicator: {
              backgroundColor: activeHex,
            },
          },
        },
        MuiCircularProgress: {
          styleOverrides: {
            colorPrimary: {
              color: activeHex,
            },
          },
        },
      },
    });
  }, [themeColor, config]);

  return (
    <ThemeContext.Provider
      value={{
        themeColor,
        config,
        gradientColor1: config?.gradientColor1,
        gradientColor2: config?.gradientColor2,
        employer,
        loadingTheme,
        updateTheme,
      }}
    >
      <MuiThemeProvider theme={muiTheme}>
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
}

export default ThemeContext;
